import { env } from '@lib/env'
import { withIsoCurrencyCode } from 'shared/src/utils/currencyUtils'

export const emptyValues = new Set([undefined, null, ''])

const PLACEHOLDER_QUERY_IDS = new Set(['null', 'undefined'])

/**
 * Query string values are always strings; callers may pass literal "null" or
 * "undefined". Treat those as absent so API paths are not built with bogus ids.
 */
export function normalizeOptionalQueryId(
  value: string | null | undefined
): string | null {
  if (value == null) return null
  const trimmed = String(value).trim()
  if (!trimmed) return null
  if (PLACEHOLDER_QUERY_IDS.has(trimmed.toLowerCase())) return null
  return trimmed
}

export function getId<T extends { id?: string; _id?: string }>(obj: T): string {
  return obj.id ?? (obj._id as string)
}

export function getIframeUrl(
  path: string,
  id: string,
  options?: { theme?: string | null }
) {
  const url = new URL(`${env.domain}${path}`)
  url.searchParams.set('iframe', id)
  if (options?.theme) url.searchParams.set('theme', options.theme)

  return url.href
}

export function nanoid(): string {
  return Math.random().toString(36).substring(2)
}

export function stripPhoneNumber(val: string | null | undefined) {
  if (!val) return ''

  return val.replace(/\D/gi, '')
}

export function formatPhoneNumber(val: string | null | undefined) {
  if (!val) return ''

  const digits = val.replace(/\D/g, '')
  if (digits.length <= 10) {
    if (digits.length > 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
    } else if (digits.length > 3) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
    } else if (digits.length > 0) {
      return `(${digits})`
    }

    return ''
  }

  const countryCode = digits.slice(0, digits.length - 10)
  const local = digits.slice(-10)

  return `+${countryCode} (${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6, 10)}`
}

export function countryCodeSafePhone(val: string | null | undefined) {
  if (!val) return ''

  return val.length === 10 ? `1${val}` : val
}

export function formatPercent(val: number | null | undefined) {
  if (!val) return '0%'

  const formatter = new Intl.NumberFormat(undefined, {
    style: 'percent',
    maximumFractionDigits: 2,
  })
  return formatter.format(val)
}

export function formatPrice(
  val: number | null | undefined,
  options?: Intl.NumberFormatOptions & { languageCode?: string | null }
) {
  if (val === null || val === undefined) return null
  else {
    const isWholeNumber = val % 1 === 0
    const languageCode = options?.languageCode ?? 'en'
    const { languageCode: _languageCode, ...intlOptions } = options ?? {}
    const requestedCurrency = intlOptions.currency
    const currency =
      typeof requestedCurrency === 'string' && requestedCurrency.trim()
        ? requestedCurrency
        : 'USD'
    const formatter = new Intl.NumberFormat(languageCode, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      ...intlOptions,
      maximumFractionDigits: isWholeNumber ? 0 : 2,
      minimumFractionDigits: isWholeNumber ? 0 : 2,
    })

    return withIsoCurrencyCode(
      formatter.format(val),
      currency
    )
  }
}

export const nthNumber = (number: number) => {
  if (number > 3 && number < 21) return 'th'
  switch (number % 10) {
    case 1:
      return 'st'
    case 2:
      return 'nd'
    case 3:
      return 'rd'
    default:
      return 'th'
  }
}
